import {
  Typography,
  Box,
  Container,
  Fade,
  Button,
  Chip,
} from "@mui/material";
import { Vocabulary } from "../../Utils/Vocabulary";
import style from "../../Styles/commonWrongQuestions.module.css";
import genericStyle from "../../Styles/genericStyles.module.css";
import { useEffect, useMemo, useState } from "react";
import { getData } from "../../Services/getData";
import { endpoints, localUrlEnum } from "../../Utils/UrlEnum";
import QuestionCollapsable from "./QuestionsCollapsable";
import { useNavigate } from "react-router-dom";
import { PlayArrow } from "@mui/icons-material";
import { GroupedExamSets } from "../../Utils/Types";
import { ExamsSetModel } from "../../Utils/Models";
import { isFreeSubscription } from "../../Utils/Utils";
import { examCategories } from "../../Utils/Constants";

export default function CommonWrongQuestions() {
  const clientId = localStorage.getItem("clientId");
  const navigate = useNavigate();
  const [wrongQuestions, setWrongQuestions] = useState<any[]>([]);
  const [groupedExamSets, setGroupedExamSets] = useState<GroupedExamSets>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const [wrongQuestionsRes, examSetsRes] = await Promise.all([
        getData(
          `${endpoints.examQuestions.commonWrongQuestions}/${clientId}`
        ),
        getData(
          `${endpoints.examSets.main}/forClient/${clientId}?isFreeSubscription=${isFreeSubscription()}`
        ),
      ]);

      if (wrongQuestionsRes?.data) {
        setWrongQuestions(wrongQuestionsRes.data);
      }
      if (examSetsRes?.data) {
        setGroupedExamSets(examSetsRes.data);
      }
      setLoading(false);
    };

    fetchData();
  }, [clientId]);

  /**
   * Build a map of examSetId -> examCategory for quick lookup.
   * Only includes active sets (those with questions) so disabled sets are excluded.
   */
  const examSetToCategoryMap = useMemo(() => {
    const map: Record<string, string> = {};
    Object.values(groupedExamSets).forEach((sets: ExamsSetModel[]) => {
      sets.forEach((set) => {
        if (
          set._id &&
          set.examCategory &&
          set.totalQuestions &&
          set.totalQuestions > 0
        ) {
          map[set._id] = set.examCategory;
        }
      });
    });
    return map;
  }, [groupedExamSets]);

  /**
   * Consistent category order based on examCategories keys
   */
  const categoryOrder = Object.keys(examCategories);

  /**
   * Group wrong questions by exam category, sorted consistently.
   * Questions with unknown categories (not in any returned exam set) are ignored.
   */
  const sortedCategoryEntries = useMemo(() => {
    const grouped: Record<string, any[]> = {};
    wrongQuestions.forEach((item: any) => {
      const question = item?.question || item;
      const examSetId = question?.examSetId;
      if (!examSetId) return;
      const category = examSetToCategoryMap[examSetId];
      if (!category) return; // ignore questions from unknown/inactive exam sets
      if (!grouped[category]) {
        grouped[category] = [];
      }
      grouped[category].push(item);
    });

    // Sort entries by the predefined category order
    return Object.entries(grouped).sort(([a], [b]) => {
      const indexA = categoryOrder.indexOf(a.toUpperCase());
      const indexB = categoryOrder.indexOf(b.toUpperCase());
      return indexA - indexB;
    });
  }, [wrongQuestions, examSetToCategoryMap, categoryOrder]);

  const getCategoryData = (categoryKey: string) => {
    return examCategories[
      categoryKey.toUpperCase() as keyof typeof examCategories
    ] as any;
  };

  /**
   * Shuffle array using Fisher-Yates algorithm
   */
  const shuffleArray = <T,>(array: T[]): T[] => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  /**
   * Start practice for a specific category —
   * limits questions to the category's totalQuestions and passes examCategory
   */
  const handleStartCategoryPractice = (
    questions: any[],
    category: string
  ) => {
    const catData = getCategoryData(category);
    const maxQuestions = catData?.totalQuestions || questions.length;

    const allPracticeQuestions = questions.map(
      (item: any) => item.question || item
    );

    // Shuffle and cap to the category's totalQuestions limit
    const limitedQuestions = shuffleArray(allPracticeQuestions).slice(
      0,
      maxQuestions
    );

    navigate(
      `${localUrlEnum.examQuestions}?examCategory=${category.toUpperCase()}`,
      {
        state: { practiceQuestions: limitedQuestions, isPracticeMode: true },
      }
    );
  };

  return (
    <div className={genericStyle.scrollableContainer}>
      <Box className={style.mainContainer}>
        <Container maxWidth="xl" className={style.questionsContainer}>
          <Fade in={!loading} timeout={800}>
            <Box>
              <Box className={style.headerSection}>
                <Box>
                  <Typography
                    variant="h2"
                    className={`${style.mainTitle} ${genericStyle.titleColor}`}
                  >
                    {Vocabulary.commonWrongQuestions}
                  </Typography>
                  <Typography variant="body1" className={style.subTitle}>
                    {Vocabulary.commonWrongQuestionsExplanation}
                  </Typography>
                </Box>
              </Box>

              {sortedCategoryEntries.length === 0 && !loading ? (
                <Box className={style.noDataContainer}>
                  <Typography variant="h6" className={style.noDataText}>
                    {Vocabulary.noWrongQuestionsYet}
                  </Typography>
                </Box>
              ) : (
                sortedCategoryEntries.map(
                  ([category, questions]) => {
                    const catData = getCategoryData(category);
                    const categoryLabel = catData?.label || category;

                    return (
                      <Box
                        key={category}
                        className={style.categorySection}
                      >
                        <Box className={style.categoryHeader}>
                          <Box className={style.categoryTitleRow}>
                            <Typography
                              variant="h5"
                              className={`${style.categoryTitle} ${genericStyle.titleColor}`}
                            >
                              {Vocabulary.category}: {categoryLabel}
                            </Typography>
                            <Chip
                              label={`${questions.length} ${
                                questions.length === 1
                                  ? Vocabulary.wrongQuestionSingular
                                  : Vocabulary.wrongQuestionPlural
                              }`}
                              className={style.categoryBadge}
                              size="small"
                            />
                          </Box>
                          <Button
                            variant="contained"
                            size="small"
                            startIcon={<PlayArrow />}
                            onClick={() =>
                              handleStartCategoryPractice(
                                questions,
                                category
                              )
                            }
                            className={style.categoryPracticeButton}
                          >
                            {Vocabulary.practiceThisCategory}
                          </Button>
                        </Box>

                        <Box className={style.questionsCollapsable}>
                          <QuestionCollapsable questions={questions} />
                        </Box>
                      </Box>
                    );
                  }
                )
              )}
            </Box>
          </Fade>
        </Container>
      </Box>
    </div>
  );
}
